import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { formatCents } from '@crave/shared';
import { AllergenList, Spicy } from '@crave/ui';
import { api } from '../api.ts';
import { cartSubtotalCents, useCart, cart, useOrderLocation } from '../lib/cart.ts';
import { LocationPicker } from '../components/LocationPicker.tsx';
import type { Menu, MenuItem } from '../lib/types.ts';

export function useMenu() {
  const locationSlug = useOrderLocation();
  return useQuery({
    queryKey: ['menu', locationSlug],
    queryFn: async () => (await api.get<Menu>(`/public/locations/${locationSlug}/menu`)).data,
    staleTime: 30_000,
  });
}

export function MenuPage() {
  const q = useMenu();
  const lines = useCart();
  const units = cart.units();
  if (q.isLoading)
    return (
      <>
        <LocationPicker />
        <p style={{ padding: 24 }}>Loading the menu…</p>
      </>
    );
  if (q.isError || !q.data)
    return (
      <>
        <LocationPicker />
        <p style={{ padding: 24, color: 'var(--crave-red)' }}>
          The menu is unavailable right now. Please try again shortly.
        </p>
      </>
    );
  const { location, categories } = q.data;
  const closed = !location.ordering_enabled || location.ordering_paused;
  const allergenNotice = q.data.allergen_notice?.trim();
  const pricesShown = location.show_prices !== false;
  // A line added while prices were hidden has no price stored (every real price is above zero).
  const subtotalKnown = pricesShown && lines.every((l) => l.unit_price_cents > 0);
  return (
    <div>
      <LocationPicker />
      {closed && (
        <div style={{ background: 'var(--mist)', padding: '12px 20px', textAlign: 'center' }}>
          {location.ordering_paused
            ? (location.paused_message ?? 'Ordering is paused for a moment.')
            : 'Online ordering opens soon. Browse the menu in the meantime.'}
        </div>
      )}
      {categories.map((cat) => (
        <section key={cat.slug} style={{ display: 'grid', gap: 16 }}>
          {cat.items.map((it) => (
            <ItemCard key={it.id} item={it} closed={closed} />
          ))}
        </section>
      ))}
      <p style={{ fontSize: 14, marginTop: 24 }}>
        <Link to="/how-to-eat" style={{ fontWeight: 600, color: 'var(--crave-red)' }}>
          New to Neapolitan? How to eat one
        </Link>
      </p>
      {allergenNotice && (
        <p style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink-soft)', marginTop: 8 }}>
          <strong style={{ color: 'var(--ink)' }}>Food allergies:</strong> {allergenNotice}
        </p>
      )}
      {units > 0 && (
        <Link
          to="/cart"
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 16,
            background: 'var(--crave-red)',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 14,
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 700,
            boxShadow: 'var(--shadow)',
          }}
        >
          <span>
            View cart · {units} {units === 1 ? 'pizza' : 'pizzas'}
          </span>
          {subtotalKnown && <span>{formatCents(cartSubtotalCents(lines))}</span>}
        </Link>
      )}
    </div>
  );
}

function ItemCard({ item, closed }: { item: MenuItem; closed: boolean }) {
  const soldOut = item.availability === 'sold_out' || item.availability === 'unavailable';
  return (
    <Link
      to={`/item/${item.slug}`}
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'grid',
        gridTemplateColumns: '112px minmax(0, 1fr)',
        gap: 16,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius)',
        padding: 12,
        opacity: soldOut ? 0.6 : 1,
      }}
    >
      <div
        style={{
          width: 112,
          height: 112,
          borderRadius: 12,
          background: 'var(--mist)',
          overflow: 'hidden',
        }}
      >
        {item.image_path && (
          <img
            src={`https://cravepizzafl.com/${item.image_path.replace(/\.jpg$/, '-800.jpg')}`}
            alt=""
            width={112}
            height={112}
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
            loading="lazy"
          />
        )}
      </div>
      <div style={{ display: 'grid', alignContent: 'start', gap: 4 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 18 }}>{item.name}</strong>
          {item.spicy && <Spicy size="sm" />}
          {item.badge && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: item.is_signature ? 'var(--crave-red)' : 'var(--ink-soft)',
              }}
            >
              {item.badge}
            </span>
          )}
        </div>
        <span style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
          {item.ingredients.join(', ')}
        </span>
        <AllergenList
          allergens={item.allergens}
          none={null}
          style={{ fontSize: 13, color: 'var(--ink-soft)' }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            marginTop: 4,
          }}
        >
          <span style={{ fontWeight: 700 }}>
            {item.price_cents !== null ? formatCents(item.price_cents) : ''}
          </span>
          <span
            style={{
              fontSize: 13,
              textAlign: 'right',
              color: soldOut ? 'var(--crave-red)' : 'var(--ink-soft)',
            }}
          >
            {item.availability === 'unavailable'
              ? (item.unavailable_reason ?? 'Unavailable today')
              : item.availability === 'sold_out'
                ? 'Sold out'
                : item.availability === 'future_only'
                  ? 'Available for future pickup'
                  : closed
                    ? ''
                    : 'Customize →'}
          </span>
        </div>
      </div>
    </Link>
  );
}
